import React, { useState, FC } from 'react';
import {
    useNotify,
    useRefresh,
    useTranslate,
    useDataProvider,
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import SendIcon from '@material-ui/icons/Send';
import ActionCheck from '@material-ui/icons/CheckCircle';
import classnames from 'classnames';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    FormControl,
    TextField,
    Select,
    MenuItem,
    InputLabel,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

const sanitizeRestProps: (any) => any = ({
    basePath,
    classes,
    filterValues,
    label,
    resource,
    selectedIds,
    ...rest
}) => rest;

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
        marginTop: 10,
        gap: 16,
    },
    sendButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: fade(theme.palette.primary.main, 0.12),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
    },
    confirmPrimary: {
        color: theme.palette.primary.main,
    },
    confirmWarning: {
        color: theme.palette.error.main,
        '&:hover': {
            backgroundColor: fade(theme.palette.error.main, 0.12),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
    },
    iconPaddingStyle: {
        paddingRight: '0.5em',
    },
    paramSection: {
        marginTop: 16,
        padding: 16,
        backgroundColor: '#fafafa',
        borderRadius: 8,
        border: '1px solid #e0e0e0',
    },
    paramHeader: {
        marginBottom: 16,
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    paramSubHeader: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '#666',
        marginTop: 12,
        marginBottom: 8,
    },
    paramRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
    },
    paramLabel: {
        minWidth: 180,
        maxWidth: 180,
        fontWeight: 500,
        fontSize: '0.875rem',
        color: '#333',
    },
    systemParamRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        backgroundColor: '#f5f5f5',
        borderRadius: 6,
        marginBottom: 8,
        border: '1px solid #e8e8e8',
    },
    systemParamLabel: {
        minWidth: 180,
        maxWidth: 180,
        fontWeight: 500,
        fontSize: '0.875rem',
        color: '#555',
    },
    systemParamValue: {
        color: '#666',
        fontSize: '0.875rem',
        fontStyle: 'italic',
    },
    userCount: {
        marginTop: 16,
        padding: 12,
        backgroundColor: '#e3f2fd',
        borderRadius: 6,
        color: '#1976d2',
        fontSize: '0.875rem',
        fontWeight: 500,
        textAlign: 'center',
    },
}));

interface BulkSendWatiTemplateProps {
    basePath?: string;
    resource?: string;
    selectedIds?: number[];
    label?: string;
    cancel?: string;
    confirm?: string;
    confirmColor?: string;
    icon?: React.ReactElement;
}

const BulkSendWatiTemplate: FC<BulkSendWatiTemplateProps> = ({
    label = 'Send Wati Template',
    cancel = 'ra.action.cancel',
    confirm = 'ra.action.confirm',
    confirmColor = 'primary',
    icon = <SendIcon />,
    basePath,
    resource,
    selectedIds,
    ...rest
}) => {
    const classes = useStyles();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templates, setTemplates] = useState<string[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [templateParams, setTemplateParams] = useState<{
        watiTemplateParamNames: string[];
        systemParamNames: string[];
    } | null>(null);
    const [loadingParams, setLoadingParams] = useState(false);
    const [paramValues, setParamValues] = useState<Record<string, string>>({});
    const [countryId, setCountryId] = useState<number | null>(null);

    const fetchUserCountry = async () => {
        if (!selectedIds || selectedIds.length === 0) return null;

        try {
            // Fetch all selected users to validate they're from the same country
            const userPromises = selectedIds.map(id =>
                dataProvider.getOne('users', { id })
            );
            const users = await Promise.all(userPromises);

            // Get all unique country IDs
            const countryIds = [...new Set(users.map(u => u.data.countryId))];

            // Check if all users are from the same country
            if (countryIds.length > 1) {
                notify(
                    'Selected users are from different countries. Please select users from the same country.',
                    'warning'
                );
                return null;
            }

            return countryIds[0];
        } catch (error) {
            console.error('Error fetching user countries:', error);
            notify('Error validating user countries', 'warning');
            return null;
        }
    };

    const fetchTemplates = async (userCountryId: number) => {
        setLoadingTemplates(true);
        try {
            const token = localStorage.getItem('feathers-jwt');
            const apiUrl =
                process.env.REACT_APP_API_URL ||
                'https://api.staging.boxitstorage.com';

            const response = await fetch(
                `${apiUrl}/api/v1/whatsapp/templates-names?countryId=${userCountryId}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch templates');
            }

            const data = await response.json();
            setTemplates(data.watiTemplateNames || []);
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Unknown error';
            notify(`Error loading templates: ${errorMessage}`, 'warning');
            setTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const fetchTemplateParams = async (templateName: string) => {
        if (!countryId) return;

        setLoadingParams(true);
        try {
            const token = localStorage.getItem('feathers-jwt');
            const apiUrl =
                process.env.REACT_APP_API_URL ||
                'https://api.staging.boxitstorage.com';

            const response = await fetch(
                `${apiUrl}/api/v1/whatsapp/templates-names/${templateName}?countryId=${countryId}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch template parameters');
            }

            const data = await response.json();
            setTemplateParams(data);

            // Initialize param values
            const initialValues: Record<string, string> = {};
            [
                ...(data.watiTemplateParamNames || []),
                ...(data.systemParamNames || []),
            ].forEach(param => {
                initialValues[param] = '';
            });
            setParamValues(initialValues);
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Unknown error';
            notify(
                `Error loading template parameters: ${errorMessage}`,
                'warning'
            );
            setTemplateParams(null);
            setParamValues({});
        } finally {
            setLoadingParams(false);
        }
    };

    const handleOpenDialog = async () => {
        // Fetch and validate user countries first
        const userCountryId = await fetchUserCountry();

        if (userCountryId) {
            setCountryId(userCountryId);
            setIsOpen(true);
            fetchTemplates(userCountryId);
        } else {
            // Dialog won't open if validation fails
            // Error notification already shown in fetchUserCountry
        }
    };

    const handleParamChange = (paramName: string, value: string) => {
        setParamValues(prev => ({
            ...prev,
            [paramName]: value,
        }));
    };

    const formatParamLabel = (param: string) => {
        // Add spaces before capital letters and handle special cases
        return param
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
            .replace(/attr /gi, '') // Remove 'attr' prefix
            .trim();
    };

    const handleTemplateNameChange = event => {
        const selectedTemplate = event.target.value;
        setTemplateName(selectedTemplate);
        if (selectedTemplate) {
            fetchTemplateParams(selectedTemplate);
        } else {
            setTemplateParams(null);
            setParamValues({});
        }
    };

    const handleConfirm = async () => {
        if (!templateName) {
            notify('Please select a template', 'warning');
            return;
        }

        if (!selectedIds || selectedIds.length === 0) {
            notify('Please select at least one user', 'warning');
            return;
        }

        if (!countryId) {
            notify('Country ID not available', 'warning');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('feathers-jwt');
            const apiUrl =
                process.env.REACT_APP_API_URL ||
                'https://api.staging.boxitstorage.com';

            const response = await fetch(
                `${apiUrl}/api/v1/whatsapp/templates-names/${templateName}/send-whatsapp-message?countryId=${countryId}`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userIds: selectedIds,
                        params: paramValues,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error('Failed to send WhatsApp messages');
            }

            const data = await response.json();

            // Display success message with details from response
            notify(
                `WhatsApp template "${data.templateName}" sent successfully to ${data.sentTo} user(s)`,
                'success'
            );

            setIsOpen(false);
            setTemplateName('');
            setTemplateParams(null);
            setParamValues({});
            refresh();
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Unknown error';
            notify(`Error sending Wati template: ${errorMessage}`, 'warning');
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <Button
                onClick={handleOpenDialog}
                className={classes.sendButton}
                {...sanitizeRestProps(rest)}
            >
                {icon}
                {translate(label, { _: label })}
            </Button>
            <Dialog
                open={isOpen}
                onClose={() => setIsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Send Wati Template</DialogTitle>
                <DialogContent>
                    <form
                        className={classes.container}
                        noValidate
                        autoComplete="off"
                    >
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel id="template-select-label">
                                Template Name
                            </InputLabel>
                            <Select
                                labelId="template-select-label"
                                id="template-select"
                                value={templateName}
                                onChange={handleTemplateNameChange}
                                label="Template Name"
                                required
                                disabled={loadingTemplates}
                            >
                                <MenuItem value="">
                                    <em>
                                        {loadingTemplates
                                            ? 'Loading templates...'
                                            : 'Select a template'}
                                    </em>
                                </MenuItem>
                                {templates.map(template => (
                                    <MenuItem key={template} value={template}>
                                        {template}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Template Parameters Section */}
                        {loadingParams && (
                            <div
                                style={{
                                    marginTop: 16,
                                    color: '#666',
                                    textAlign: 'center',
                                    padding: 12,
                                }}
                            >
                                Loading template parameters...
                            </div>
                        )}

                        {templateParams && !loadingParams && (
                            <div className={classes.paramSection}>
                                <div className={classes.paramHeader}>
                                    Template Parameters
                                </div>

                                {/* Wati Template Parameters */}
                                {templateParams.watiTemplateParamNames &&
                                    templateParams.watiTemplateParamNames
                                        .length > 0 && (
                                        <>
                                            <div
                                                className={
                                                    classes.paramSubHeader
                                                }
                                            >
                                                Template Parameters (Required)
                                            </div>
                                            {templateParams.watiTemplateParamNames.map(
                                                param => (
                                                    <div
                                                        key={param}
                                                        className={
                                                            classes.paramRow
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                classes.paramLabel
                                                            }
                                                        >
                                                            {formatParamLabel(
                                                                param
                                                            )}
                                                        </span>
                                                        <Autocomplete
                                                            freeSolo
                                                            options={
                                                                templateParams.systemParamNames ||
                                                                []
                                                            }
                                                            value={
                                                                paramValues[
                                                                    param
                                                                ] || ''
                                                            }
                                                            onChange={(
                                                                event,
                                                                newValue
                                                            ) => {
                                                                handleParamChange(
                                                                    param,
                                                                    newValue ||
                                                                        ''
                                                                );
                                                            }}
                                                            onInputChange={(
                                                                event,
                                                                newInputValue
                                                            ) => {
                                                                handleParamChange(
                                                                    param,
                                                                    newInputValue
                                                                );
                                                            }}
                                                            renderInput={params => (
                                                                <TextField
                                                                    {...params}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    placeholder={`Enter ${formatParamLabel(
                                                                        param
                                                                    ).toLowerCase()}`}
                                                                    required
                                                                />
                                                            )}
                                                            style={{
                                                                width: 300,
                                                            }}
                                                        />
                                                    </div>
                                                )
                                            )}
                                        </>
                                    )}
                            </div>
                        )}

                        <div className={classes.userCount}>
                            📤 Sending to {selectedIds?.length || 0} selected
                            user(s)
                        </div>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsOpen(false)} color="default">
                        {translate(cancel, { _: cancel })}
                    </Button>
                    <Button
                        disabled={loading || !templateName}
                        onClick={handleConfirm}
                        className={classnames('ra-confirm', {
                            [classes.confirmWarning]:
                                confirmColor === 'warning',
                            [classes.confirmPrimary]:
                                confirmColor === 'primary',
                        })}
                        autoFocus
                    >
                        <ActionCheck className={classes.iconPaddingStyle} />
                        {loading
                            ? 'Sending...'
                            : translate(confirm, { _: confirm })}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default BulkSendWatiTemplate;
